import { useRef, useState, useMemo, useCallback, useEffect } from "react";
import type { CatalogItem } from "@apex/shared";
import { cn, useCatalogItemCompatibility } from "@apex/shared";
import { useWidget } from "../state";
import { CatalogItemRow } from "./CatalogItemRow";
import { CategorySection } from "./CategorySection";
import { RuleFiredToast } from "./RuleFiredToast";
import { containerIsScroller, readStickyOffsetPx } from "../utils/scroll";

export interface CatalogItemListProps {
  className?: string;
  /** When set, the category nav + heading become sticky at this top offset (px) */
  stickyNavTop?: number;
}

export function CatalogItemList({ className, stickyNavTop }: CatalogItemListProps) {
  const {
    state,
    toggleItem,
    setSelectedItems,
    setAccessorySearchTerm,
    scrollToItem,
    registerScrollToItem,
  } = useWidget();
  const { items, categories, selectedItems, itemRules, accessorySearchTerm } = state;
  const sectionRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const itemRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  // Client-side accessory search — filters by name + code + description.
  // Multi-word query ANDs the terms; categories with no matches drop out.
  const filteredItems = useMemo(() => {
    const term = accessorySearchTerm.trim().toLowerCase();
    if (term.length === 0) return items;
    const tokens = term.split(/\s+/).filter(Boolean);
    return items.filter((item) => {
      const haystack = [
        item.name ?? "",
        item.code,
        item.description ?? "",
      ]
        .join(" ")
        .toLowerCase();
      return tokens.every((t) => haystack.includes(t));
    });
  }, [items, accessorySearchTerm]);

  // Transient toast queue — surfaces rule-driven selection changes (auto-add
  // or auto-remove triggered by another item). Each entry has a unique key so
  // multiple rules firing in the same tick stack rather than overwriting.
  const [toasts, setToasts] = useState<{ key: number; message: string }[]>([]);
  const toastKeyRef = useRef(0);
  const dismissToast = useCallback((key: number) => {
    setToasts((prev) => prev.filter((t) => t.key !== key));
  }, []);
  const pushToast = useCallback((message: string) => {
    toastKeyRef.current += 1;
    const key = toastKeyRef.current;
    setToasts((prev) => [...prev, { key, message }]);
  }, []);

  // Use catalog item compatibility hook (rule-based)
  const { getStatus, getDisabledReason, getRelatedItems } = useCatalogItemCompatibility({
    items,
    rules: itemRules,
    selectedIds: Array.from(selectedItems),
    onAutoDeselect: (removedIds) => {
      // Remove auto-deselected items
      const newSelection = Array.from(selectedItems).filter(
        (id) => !removedIds.includes(id)
      );
      setSelectedItems(newSelection);

      // Toast: name the auto-removed items so the customer knows why their
      // earlier selection just disappeared.
      for (const removedId of removedIds) {
        const removed = items.find((i) => i.id === removedId);
        if (removed) {
          pushToast(`${removed.name ?? removed.code} removed - rule conflict`);
        }
      }
    },
  });

  // For each currently-selected item, find the active "requires" rules that
  // have it as the target — i.e. items that selected because something else
  // requires them. Surface those drivers via the row's "Auto-selected with"
  // badge so the user can see why a row is checked.
  const autoSelectedByMap = useMemo(() => {
    const out = new Map<string, { id: string; code: string; name: string }[]>();
    if (selectedItems.size === 0 || itemRules.length === 0) return out;
    const itemById = new Map(items.map((i) => [i.id, i]));
    for (const rule of itemRules) {
      if (rule.ruleType !== "requires") continue;
      if (!selectedItems.has(rule.targetItemId)) continue;
      if (!selectedItems.has(rule.sourceItemId)) continue;
      const source = itemById.get(rule.sourceItemId);
      if (!source) continue;
      const list = out.get(rule.targetItemId) ?? [];
      list.push({
        id: source.id,
        code: source.code,
        name: source.name ?? source.code,
      });
      out.set(rule.targetItemId, list);
    }
    return out;
  }, [items, itemRules, selectedItems]);

  // Group filtered items by category. When `accessorySearchTerm` filters out
  // every item in a category, that group never gets populated and the empty
  // groups are dropped at the end — so the category disappears entirely from
  // the UI rather than rendering an empty section.
  const groupedItems = useMemo(() => {
    const groups = new Map<string, CatalogItem[]>();

    // Sort categories by display order
    const sortedCategories = [...categories].sort(
      (a, b) => a.displayOrder - b.displayOrder
    );

    // Initialize groups for each category
    for (const category of sortedCategories) {
      groups.set(String(category.id), []);
    }
    groups.set("__other__", []); // Uncategorized

    // Assign items to groups
    for (const item of filteredItems) {
      const categoryId = item.categoryId != null
        ? String(item.categoryId)
        : "__other__";
      const group = groups.get(categoryId);
      if (group) {
        group.push(item);
      } else {
        groups.get("__other__")?.push(item);
      }
    }

    // Remove empty groups
    for (const [key, value] of groups) {
      if (value.length === 0) {
        groups.delete(key);
      }
    }

    return groups;
  }, [filteredItems, categories]);

  // Get category name by ID
  const getCategoryName = useCallback(
    (categoryId: string): string => {
      if (categoryId === "__other__") return "Other";
      const category = categories.find((c) => String(c.id) === categoryId);
      return category?.name || "Other";
    },
    [categories]
  );

  // Category tabs with selection counts
  const categoryTabs = useMemo(() => {
    const selectedSet = new Set(Array.from(selectedItems));
    return Array.from(groupedItems.entries()).map(([id, categoryItems]) => ({
      id,
      name: getCategoryName(id),
      total: categoryItems.length,
      selected: categoryItems.filter((a) => selectedSet.has(a.id)).length,
    }));
  }, [groupedItems, getCategoryName, selectedItems]);

  // Ref for measuring the sticky nav height
  const navRef = useRef<HTMLDivElement>(null);
  const chipRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const [activeCategoryId, setActiveCategoryId] = useState<string | null>(null);
  // Flag to suppress observer updates during programmatic scroll
  const isScrollingToCategory = useRef(false);

  // Helper: center a chip in the nav
  const scrollChipIntoView = useCallback((categoryId: string) => {
    const chip = chipRefs.current.get(categoryId);
    const nav = navRef.current;
    if (chip && nav) {
      const chipRect = chip.getBoundingClientRect();
      const navRect = nav.getBoundingClientRect();
      const targetScrollLeft = nav.scrollLeft + (chipRect.left - navRect.left) - (navRect.width / 2) + (chipRect.width / 2);
      nav.scrollTo({ left: Math.max(0, targetScrollLeft), behavior: "smooth" });
    }
  }, []);

  // Scroll-based active category tracking. The scrollport is the internal
  // container in fixed mode, or the page (window) in auto mode — listen on
  // whichever actually scrolls. Section positions are read viewport-relative so
  // the same threshold math works for both.
  useEffect(() => {
    const sections = sectionRefs.current;
    if (sections.size === 0) return;

    const firstSection = sections.values().next().value;
    if (!firstSection) return;
    const container = firstSection.closest("[data-scroll-container]") as HTMLElement | null;
    if (!container) return;

    const useContainer = containerIsScroller(container);
    const scrollTarget: HTMLElement | Window = useContainer ? container : window;

    const onScroll = () => {
      if (isScrollingToCategory.current) return;

      const navHeight = navRef.current?.getBoundingClientRect().height ?? 40;
      const STICKY_BAR_HEIGHT = 53;
      const hostOffset = readStickyOffsetPx(navRef.current);
      // viewport-relative line just below the sticky stack
      const base = useContainer ? container.getBoundingClientRect().top : hostOffset;
      const threshold = base + STICKY_BAR_HEIGHT + navHeight + 16;

      const atBottom = useContainer
        ? Math.abs(container.scrollHeight - container.scrollTop - container.clientHeight) < 2
        : window.scrollY + window.innerHeight >= document.documentElement.scrollHeight - 2;

      const allIds = Array.from(sections.keys());
      let bestId: string | null = null;

      if (atBottom) {
        bestId = allIds[allIds.length - 1] ?? null;
      } else {
        for (const [id, el] of sections) {
          if (el.getBoundingClientRect().top <= threshold) {
            bestId = id;
          }
        }
        if (!bestId && allIds.length > 0) {
          bestId = allIds[0];
        }
      }

      if (bestId && bestId !== activeCategoryId) {
        setActiveCategoryId(bestId);
        scrollChipIntoView(bestId);
      }
    };

    scrollTarget.addEventListener("scroll", onScroll, { passive: true });
    // Run once to set initial active
    onScroll();

    return () => scrollTarget.removeEventListener("scroll", onScroll);
  }, [groupedItems, stickyNavTop, activeCategoryId, scrollChipIntoView]);

  // Scroll a category section to just below the sticky stack. Uses
  // scrollIntoView + scroll-margin-top so it works whether the page (auto mode)
  // or the internal container (fixed mode) is the scrollport. The margin
  // reserves room for the host header (--apex-sticky-offset), the compact bar
  // (53px, shown once scrolled) and the chip bar.
  const scrollToCategory = useCallback((categoryId: string) => {
    const el = sectionRefs.current.get(categoryId);
    if (!el) return;

    // Set active immediately and suppress the observer during the scroll
    setActiveCategoryId(categoryId);
    isScrollingToCategory.current = true;
    scrollChipIntoView(categoryId);

    const navHeight = navRef.current?.getBoundingClientRect().height ?? 40;
    const STICKY_BAR_HEIGHT = 53;
    const hostOffset = readStickyOffsetPx(navRef.current);
    el.style.scrollMarginTop = `${hostOffset + STICKY_BAR_HEIGHT + navHeight + 8}px`;
    el.scrollIntoView({ behavior: "smooth", block: "start" });

    // Re-enable the active-tracking observer once the smooth scroll settles.
    setTimeout(() => {
      isScrollingToCategory.current = false;
    }, 700);
  }, [scrollChipIntoView]);

  // Ref callback for category sections
  const setSectionRef = useCallback((categoryId: string, el: HTMLDivElement | null) => {
    if (el) {
      sectionRefs.current.set(categoryId, el);
    } else {
      sectionRefs.current.delete(categoryId);
    }
  }, []);

  // Ref callback for item rows
  const setItemRef = useCallback((id: string, el: HTMLDivElement | null) => {
    if (el) {
      itemRefs.current.set(id, el);
    } else {
      itemRefs.current.delete(id);
    }
  }, []);

  // Register scroll-to-item handler with the widget context
  useEffect(() => {
    registerScrollToItem((id: string) => {
      const el = itemRefs.current.get(id);
      if (!el) return;

      // Same scrollport-agnostic approach as scrollToCategory.
      const navHeight = navRef.current?.getBoundingClientRect().height ?? 40;
      const STICKY_BAR_HEIGHT = 53;
      const hostOffset = readStickyOffsetPx(navRef.current);
      el.style.scrollMarginTop = `${hostOffset + STICKY_BAR_HEIGHT + navHeight + 12}px`;
      el.scrollIntoView({ behavior: "smooth", block: "start" });

      // Flash highlight
      el.style.transition = "box-shadow 0.2s ease";
      el.style.boxShadow = "0 0 0 2px hsl(var(--apex-primary))";
      setTimeout(() => {
        el.style.boxShadow = "";
        setTimeout(() => { el.style.transition = ""; }, 300);
      }, 1500);
    });
  }, [registerScrollToItem]);

  if (items.length === 0) {
    return (
      <div className="apex-flex apex-flex-col apex-items-center apex-justify-center apex-py-12 apex-text-center">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="48"
          height="48"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="apex-text-muted-foreground apex-mb-4"
        >
          <path d="m7.5 4.27 9 5.15" />
          <path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" />
          <path d="m3.3 7 8.7 5 8.7-5" />
          <path d="M12 22V12" />
        </svg>
        <p className="apex-text-muted-foreground">
          No items available for this selection.
        </p>
      </div>
    );
  }

  const isSticky = stickyNavTop != null;
  const hasActiveSearch = accessorySearchTerm.trim().length > 0;
  const noMatches = hasActiveSearch && filteredItems.length === 0;
  const matchCount = filteredItems.length;

  return (
    <div className={cn("apex-space-y-4", className)}>
      {/* aria-live region — announces matched count after a search filter
          applies. Always rendered while a search is active so SR users hear
          the result count, not just the layout shift. */}
      {hasActiveSearch && (
        <p
          role="status"
          aria-live="polite"
          className="apex-sr-only"
        >
          {matchCount === 1
            ? "1 accessory matching"
            : `${matchCount} accessories matching`}
        </p>
      )}

      {/* No-match empty state — only fires when the filter dropped everything.
          Distinct from the "no items at all" early-return above. */}
      {noMatches && (
        <div className="apex-flex apex-flex-col apex-items-center apex-justify-center apex-py-12 apex-text-center apex-gap-3">
          <p className="apex-text-sm apex-text-muted-foreground">
            No accessories matching &ldquo;{accessorySearchTerm}&rdquo;
          </p>
          <button
            type="button"
            onClick={() => setAccessorySearchTerm("")}
            className="apex-inline-flex apex-items-center apex-rounded-md apex-border apex-border-input apex-bg-background apex-px-3 apex-py-1.5 apex-text-sm apex-font-medium hover:apex-bg-muted apex-transition-colors"
          >
            Clear search
          </button>
        </div>
      )}

      {/* Category chips — single-line horizontal scroll */}
      {!noMatches && categoryTabs.length > 1 && (
        <div
          ref={navRef}
          data-testid="category-nav"
          className="apex-border-b apex-border-border"
          style={{
            ...(isSticky ? {
              position: "sticky" as const,
              // Offset below the host page's fixed header (--apex-sticky-offset,
              // set via the `sticky-top` attribute) plus the compact bar gap.
              top: `calc(var(--apex-sticky-offset, 0px) + ${stickyNavTop}px)`,
              // Above the rows' z-10 controls (checkbox, info button), which
              // otherwise paint through the bar as they scroll beneath it.
              zIndex: 20,
              backgroundColor: "hsl(var(--apex-background))",
              marginLeft: "-1rem",
              marginRight: "-1rem",
              paddingLeft: "1rem",
              paddingRight: "1rem",
              paddingTop: "0.5rem",
            } : {}),
            display: "flex",
            gap: "0.5rem",
            paddingBottom: "0.5rem",
            overflowX: "auto",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            WebkitOverflowScrolling: "touch",
            // Fade right edge to hint at scrollability
            maskImage: "linear-gradient(to right, black 0, black calc(100% - 2rem), transparent 100%)",
            WebkitMaskImage: "linear-gradient(to right, black 0, black calc(100% - 2rem), transparent 100%)",
          }}
        >
          {categoryTabs.map((tab) => {
            const isActive = activeCategoryId === tab.id;
            return (
              <button
                type="button"
                key={tab.id}
                ref={(el) => {
                  if (el) chipRefs.current.set(tab.id, el);
                  else chipRefs.current.delete(tab.id);
                }}
                onClick={() => scrollToCategory(tab.id)}
                className={cn(
                  "apex-shrink-0 apex-px-3 apex-py-1.5 apex-rounded-full apex-text-sm apex-font-medium apex-transition-all",
                  "apex-flex apex-items-center apex-gap-2",
                )}
                style={{
                  backgroundColor: isActive
                    ? "hsl(var(--apex-primary))"
                    : "hsl(var(--apex-muted))",
                  color: isActive
                    ? "hsl(var(--apex-primary-foreground))"
                    : "hsl(var(--apex-muted-foreground))",
                  boxShadow: isActive ? "0 2px 8px hsl(var(--apex-primary) / 0.3)" : "none",
                  transform: isActive ? "scale(1.02)" : "scale(1)",
                  transition: "all 0.2s cubic-bezier(0.16, 1, 0.3, 1)",
                }}
              >
                {tab.name}
                {tab.selected > 0 && (
                  <span
                    className="apex-px-1.5 apex-py-0.5 apex-rounded-full apex-text-xs"
                    style={{
                      backgroundColor: isActive
                        ? "hsla(var(--apex-primary-foreground) / 0.25)"
                        : "hsl(var(--apex-primary))",
                      color: isActive
                        ? "hsl(var(--apex-primary-foreground))"
                        : "hsl(var(--apex-primary-foreground))",
                    }}
                  >
                    {tab.selected}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Items — each category becomes a collapsible CategorySection.
          When `accessorySearchTerm` is non-empty, every visible category is
          force-expanded so the user sees their search results in context.
          When the search clears, each section reverts to the user's last
          toggle preference. */}
      <div className="apex-space-y-4">
        {Array.from(groupedItems.entries()).map(([categoryId, categoryItems]) => {
          const selectedInCategory = categoryItems.filter((it) =>
            selectedItems.has(it.id),
          ).length;
          return (
            <div
              key={categoryId}
              ref={(el) => setSectionRef(categoryId, el)}
              data-category-id={categoryId}
            >
              <CategorySection
                title={getCategoryName(categoryId)}
                itemCount={categoryItems.length}
                selectedCount={selectedInCategory}
                forceOpen={hasActiveSearch}
                onSelectedBadgeClick={() => {
                  const firstSelected = categoryItems.find((it) =>
                    selectedItems.has(it.id),
                  );
                  if (firstSelected) scrollToItem(firstSelected.id);
                }}
              >
                <div className="apex-space-y-2 apex-pt-2">
                  {categoryItems.map((item) => (
                    <CatalogItemRow
                      key={item.id}
                      ref={(el) => setItemRef(item.id, el)}
                      item={item}
                      status={getStatus(item.id)}
                      reason={getDisabledReason(item.id)}
                      relatedItems={getRelatedItems(item.id)}
                      autoSelectedBy={autoSelectedByMap.get(item.id)}
                      onToggle={toggleItem}
                      onRelatedClick={scrollToItem}
                    />
                  ))}
                </div>
              </CategorySection>
            </div>
          );
        })}
      </div>

      {/* Toast stack — fixed at the bottom of the configure view, dismisses
          after 4s. Multiple rules firing at once stack vertically. */}
      {toasts.length > 0 && (
        <div
          aria-live="polite"
          className="apex-fixed apex-bottom-4 apex-left-1/2 apex--translate-x-1/2 apex-z-40 apex-flex apex-flex-col apex-gap-2 apex-pointer-events-none"
        >
          {toasts.map((t) => (
            <RuleFiredToast
              key={t.key}
              message={t.message}
              onClose={() => dismissToast(t.key)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
